import { Stack } from '@mui/material';
import { FlowFhirResourceType, FlowHieDocumentFormat } from 'flow-schema';
import { type ComponentType } from 'react';
import type { z } from 'zod';

import { api } from 'common';

import { useReactFlowViewContext } from '../../../context/context';
import { BlockAutocomplete } from '../../ConfigFields/Autocomplete';
import { BlockValueTemplateField } from '../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockSwitchField } from '../../ConfigFields/Switch';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

const HieGetDocuments = api.FlowDefinition.HieGetDocuments;

interface HieGetDocumentVariant {
  schema: z.ZodObject<any>;
  Config: ComponentType | null;
}

const FHIR_RESOURCE_OPTIONS = Object.values(FlowFhirResourceType);

function ConsolidatedDataConfig() {
  const { isEditable } = useReactFlowViewContext();
  const { value: format } = useSelectedNodeField<string>('config.documentFormat');

  return (
    <Stack spacing={2}>
      <BlockAutocomplete
        fieldName="config.resources"
        label="Resources"
        options={FHIR_RESOURCE_OPTIONS}
        multiple
        helperText="Select one or more resource types, or 'All' for the complete report"
        disabled={!isEditable}
        getOptionLabel={(v) => v.replace(/([A-Z])/g, ' $1').trim()}
      />

      {format === FlowHieDocumentFormat.ConsolidatedData && (
        <BlockSwitchField
          fieldName="config.includeRelatedRecords"
          label="Include Related Records"
          helperText="Include records referenced by the selected resource type. For example, if an Encounter record contains a reference to a Practitioner, the Practitioner record will also be included."
        />
      )}
      <BlockValueTemplateField
        fieldName="config.dateFrom"
        label="Date From"
        helperText="The start date (inclusive) for which to filter returned resources - formatted YYYY-MM-DD as per ISO 8601. If not provided, no start date filter will be applied."
      />
      <BlockValueTemplateField
        fieldName="config.dateTo"
        label="Date To"
        helperText="The end date (inclusive) for which to filter returned resources - formatted YYYY-MM-DD as per ISO 8601. If not provided, no end date filter will be applied."
      />
    </Stack>
  );
}

export const HIE_GET_DOCUMENTS_FORMAT_VARIANTS: Record<
  keyof typeof FlowHieDocumentFormat,
  HieGetDocumentVariant
> = {
  [FlowHieDocumentFormat.ConsolidatedData]: {
    schema: HieGetDocuments.ConsolidatedJsonConfigSchema,
    Config: ConsolidatedDataConfig,
  },
  [FlowHieDocumentFormat.ConsolidatedDataPdf]: {
    schema: HieGetDocuments.ConsolidatedRenderedConfigSchema,
    Config: ConsolidatedDataConfig,
  },
  [FlowHieDocumentFormat.ConsolidatedDataHtml]: {
    schema: HieGetDocuments.ConsolidatedRenderedConfigSchema,
    Config: ConsolidatedDataConfig,
  },
  [FlowHieDocumentFormat.BulkDownloadUrls]: {
    schema: HieGetDocuments.BulkDownloadUrlsConfigSchema,
    Config: null,
  },
};
